/**
 * A chosen video's length and a still from it, read on the phone before
 * anything is sent: the API cannot play a video, so the 30-second limit and
 * the thumbnail are both worked out here (step 7a).
 *
 * The still is drawn from a moment just in, scaled to 240 px wide, as a JPEG.
 * Safari will not draw a frame it has not decoded, so the video is muted,
 * inline, and seeked before drawing. If no frame can be drawn the still is a
 * plain dark one, rather than refusing a video that is otherwise fine.
 *
 * AND PLAYED FIRST (found 25 October): on an iPhone - Safari, and Chrome,
 * which is Safari underneath - a video that has never played draws black
 * however far it is seeked, so both of Matthew's videos to the Kellet lesson
 * got the plain still (2,085 bytes each, read from media_asset). So the video
 * is put on the page, out of sight, and played muted for a moment before the
 * seek; a play the browser refuses is let go, and the seek tried as before.
 */

export interface Probed {
  durationSeconds: number;
  thumbnail: Blob;
}

const WIDTH = 240;

export async function probeVideo(file: File): Promise<Probed> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none';
    document.body.appendChild(video);
    video.src = url;

    await once(video, 'loadedmetadata');
    const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;

    try {
      // Never waited on for long: a play that neither starts nor fails must not
      // hold up the send.
      await Promise.race([
        video.play(),
        new Promise((_, reject) => window.setTimeout(() => reject(new Error('play never started')), 3_000)),
      ]);
      video.pause();
    } catch {
      // Refused (no autoplay here): seek without it, as before.
    }

    // A clip that opens on black, or a browser that reads the length but cannot
    // decode the picture (an iPhone's HEVC in Chrome on Windows - found 16
    // October), draws black. Later moments are tried before settling for that.
    let thumbnail: Blob | null = null;
    for (const at of [0.5, 1.5, durationSeconds / 2]) {
      try {
        video.currentTime = Math.min(at, durationSeconds / 2);
        await once(video, 'seeked');
        const still = drawStill(video);
        if (still && !still.dark) {
          thumbnail = await still.blob;
          break;
        }
      } catch {
        break;
      }
    }

    return { durationSeconds, thumbnail: thumbnail ?? (await plainStill()) };
  } finally {
    video.pause();
    video.remove();
    URL.revokeObjectURL(url);
  }
}

function once(target: HTMLVideoElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${event} never came`)), 15_000);
    target.addEventListener(event, () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
    target.addEventListener('error', () => {
      window.clearTimeout(timer);
      reject(new Error('the video could not be read'));
    }, { once: true });
  });
}

/** The frame on screen now, and whether it is all but black. */
function drawStill(video: HTMLVideoElement): { blob: Promise<Blob | null>; dark: boolean } | null {
  const scale = video.videoWidth ? WIDTH / video.videoWidth : 1;
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = Math.round((video.videoHeight || 320) * scale) || 320;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  let brightest = 0;
  for (let i = 0; i < pixels.length; i += 4 * 97) {
    brightest = Math.max(brightest, pixels[i] ?? 0, pixels[i + 1] ?? 0, pixels[i + 2] ?? 0);
  }

  return { blob: new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8)), dark: brightest < 16 };
}

function plainStill(): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = 320;
  const context = canvas.getContext('2d');
  if (context) {
    context.fillStyle = '#2B3440';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('no still'))), 'image/jpeg', 0.8),
  );
}
